// Intake.AI - Lead Detail Page
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ChevronLeft,
  UserPlus,
  Phone,
  Mail,
  Building,
  MapPin,
  Calendar,
  Mic,
  MicOff,
  Square,
  Play,
  Pause,
  FileText,
  Upload,
  Send,
  Brain,
  Clock,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  Loader2,
  Download,
  Trash2,
  Link,
  Copy,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [config, setConfig] = useState(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [showRecordingDialog, setShowRecordingDialog] = useState(false);
  const [wiretapAcknowledged, setWiretapAcknowledged] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  
  // Upload link state
  const [uploadLinks, setUploadLinks] = useState([]);
  const [showUploadLinkDialog, setShowUploadLinkDialog] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchUploadLinks();
  }, [leadId, token]);

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch lead
      const leadRes = await fetch(`${API_URL}/api/intake/leads/${leadId}`, { headers });
      if (leadRes.ok) {
        setLead(await leadRes.json());
      } else {
        toast.error('Lead not found');
        navigate('/intake');
        return;
      }
      
      // Fetch consultations
      const consultRes = await fetch(`${API_URL}/api/intake/leads/${leadId}/consultations`, { headers });
      if (consultRes.ok) setConsultations(await consultRes.json());
      
      // Fetch documents
      const docsRes = await fetch(`${API_URL}/api/intake/leads/${leadId}/documents`, { headers });
      if (docsRes.ok) setDocuments(await docsRes.json());
      
      // Fetch config
      const configRes = await fetch(`${API_URL}/api/intake/config`, { headers });
      if (configRes.ok) setConfig(await configRes.json());
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load lead data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadLinks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/leads/${leadId}/upload-links`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setUploadLinks(await res.json());
      }
    } catch (error) {
      console.error('Error fetching upload links:', error);
    }
  };

  const generateUploadLink = async () => {
    setGeneratingLink(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/leads/${leadId}/upload-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_id: leadId,
          expires_in_days: 7,
          max_files: 10
        })
      });
      
      if (res.ok) {
        const link = await res.json();
        setUploadLinks([link, ...uploadLinks]);
        toast.success('Upload link generated');
        setShowUploadLinkDialog(true);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to generate link');
      }
    } catch (error) {
      toast.error('Failed to generate upload link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard');
  };

  const deactivateUploadLink = async (linkId) => {
    try {
      const res = await fetch(`${API_URL}/api/intake/upload-links/${linkId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setUploadLinks(uploadLinks.map(l => 
          l.id === linkId ? { ...l, is_active: false } : l
        ));
        toast.success('Upload link deactivated');
      }
    } catch (error) {
      toast.error('Failed to deactivate link');
    }
  };

  const startRecording = async () => {
    if (!wiretapAcknowledged) {
      toast.error('Please acknowledge the recording notice first');
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
    } catch (error) {
      toast.error('Failed to access microphone. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const saveRecording = async () => {
    if (!audioBlob) return;
    
    try {
      // Create consultation first
      const consultRes = await fetch(`${API_URL}/api/intake/leads/${leadId}/consultations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_id: leadId,
          consultation_type: 'phone',
          notes: `Recording duration: ${formatTime(recordingTime)}`
        })
      });
      
      if (!consultRes.ok) {
        toast.error('Failed to create consultation');
        return;
      }
      
      const consultation = await consultRes.json();
      
      // Upload audio
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const uploadRes = await fetch(`${API_URL}/api/intake/consultations/${consultation.id}/upload-audio`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (uploadRes.ok) {
        toast.success('Recording saved successfully');
        setShowRecordingDialog(false);
        setAudioBlob(null);
        setRecordingTime(0);
        setWiretapAcknowledged(false);
        fetchData();
      } else {
        toast.error('Failed to upload recording');
      }
      
    } catch (error) {
      toast.error('Failed to save recording');
    }
  };

  const transcribeConsultation = async (consultationId) => {
    setTranscribing(true);
    setSelectedConsultation(consultationId);
    
    try {
      const res = await fetch(`${API_URL}/api/intake/consultations/${consultationId}/transcribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Transcription complete');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to transcribe');
      }
    } catch (error) {
      toast.error('Failed to transcribe recording');
    } finally {
      setTranscribing(false);
      setSelectedConsultation(null);
    }
  };

  const analyzeConsultation = async (consultationId) => {
    setAnalyzing(true);
    setSelectedConsultation(consultationId);
    
    try {
      const res = await fetch(`${API_URL}/api/intake/consultations/${consultationId}/analyze`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('AI analysis complete');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to analyze');
      }
    } catch (error) {
      toast.error('Failed to analyze consultation');
    } finally {
      setAnalyzing(false);
      setSelectedConsultation(null);
    }
  };

  const uploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploaded_by', 'firm');
    
    try {
      const res = await fetch(`${API_URL}/api/intake/leads/${leadId}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success('Document uploaded');
        fetchData();
      } else {
        toast.error('Failed to upload document');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-center">
          <UserPlus className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading lead details...</p>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  const contact = lead.contact;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="lead-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="flex h-16 items-center px-4 lg:px-8">
          <RouterLink to="/intake" className="flex items-center gap-2 mr-8">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Pipeline
            </Button>
          </RouterLink>
          
          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold">{contact?.full_name || 'Lead Details'}</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline">{lead.case_type || 'No case type'}</Badge>
              <span>•</span>
              <span>{lead.source?.replace(/_/g, ' ')}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Client Upload Link Button */}
            <Dialog open={showUploadLinkDialog} onOpenChange={setShowUploadLinkDialog}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  onClick={uploadLinks.length === 0 || !uploadLinks.some(l => l.is_active) ? generateUploadLink : undefined}
                  disabled={generatingLink}
                >
                  {generatingLink ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Link className="w-4 h-4 mr-2" />
                  )}
                  Client Upload Link
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Client Document Upload Link</DialogTitle>
                  <DialogDescription>
                    Share this secure link with your client so they can upload documents directly
                  </DialogDescription>
                </DialogHeader>
                
                {uploadLinks.length > 0 && (
                  <div className="space-y-3 my-4">
                    {uploadLinks.filter(l => l.is_active).slice(0, 3).map((link) => (
                      <div key={link.id} className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                            Active
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {link.files_uploaded}/{link.max_files} files uploaded
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input 
                            value={link.upload_url} 
                            readOnly 
                            className="text-sm font-mono bg-white"
                          />
                          <Button size="sm" variant="outline" onClick={() => copyToClipboard(link.upload_url)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => window.open(link.upload_url, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                          <span>Expires: {new Date(link.expires_at).toLocaleDateString()}</span>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-600 h-auto p-0"
                            onClick={() => deactivateUploadLink(link.id)}
                          >
                            Deactivate
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowUploadLinkDialog(false)}>
                    Close
                  </Button>
                  <Button onClick={generateUploadLink} disabled={generatingLink}>
                    {generatingLink ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Link className="w-4 h-4 mr-2" />
                    )}
                    Generate New Link
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={showRecordingDialog} onOpenChange={setShowRecordingDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Mic className="w-4 h-4 mr-2" />
                  Record Consultation
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Record Consultation</DialogTitle>
                  <DialogDescription>
                    Record a phone call or in-person meeting with this lead
                  </DialogDescription>
                </DialogHeader>
                
                {/* Wiretap Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-800 mb-1">Recording Notice Required</p>
                      <p className="text-amber-700">
                        Before recording, you must inform all parties that an AI note-taking device 
                        is listening to the conversation. Failure to do so may violate wiretap statutes 
                        in your jurisdiction.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 ml-8">
                    <Checkbox
                      id="wiretap-ack"
                      checked={wiretapAcknowledged}
                      onCheckedChange={setWiretapAcknowledged}
                    />
                    <Label htmlFor="wiretap-ack" className="text-sm text-amber-800 cursor-pointer">
                      I have informed all parties that this call/meeting is being recorded
                    </Label>
                  </div>
                </div>

                {/* Recording Controls */}
                <div className="text-center py-6">
                  {!audioBlob ? (
                    <>
                      <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 ${isRecording ? 'bg-red-100 animate-pulse' : 'bg-slate-100'}`}>
                        {isRecording ? (
                          <MicOff className="w-10 h-10 text-red-600" />
                        ) : (
                          <Mic className="w-10 h-10 text-slate-600" />
                        )}
                      </div>
                      <p className="text-3xl font-mono mb-4">{formatTime(recordingTime)}</p>
                      {isRecording ? (
                        <Button variant="destructive" size="lg" onClick={stopRecording}>
                          <Square className="w-4 h-4 mr-2" />
                          Stop Recording
                        </Button>
                      ) : (
                        <Button 
                          size="lg" 
                          onClick={startRecording}
                          disabled={!wiretapAcknowledged}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Mic className="w-4 h-4 mr-2" />
                          Start Recording
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 bg-green-100">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                      <p className="text-lg mb-2">Recording Complete</p>
                      <p className="text-muted-foreground mb-4">Duration: {formatTime(recordingTime)}</p>
                      <div className="flex justify-center gap-2">
                        <Button variant="outline" onClick={() => { setAudioBlob(null); setRecordingTime(0); }}>
                          Record Again
                        </Button>
                        <Button onClick={saveRecording} className="bg-emerald-600 hover:bg-emerald-700">
                          Save Recording
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Button onClick={() => navigate(`/intake/leads/${leadId}/agreement`)} className="bg-emerald-600 hover:bg-emerald-700">
              <Send className="w-4 h-4 mr-2" />
              Send Agreement
            </Button>
          </div>
        </div>
      </header>

      <div className="p-4 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Contact Info & AI Summary */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {contact?.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${contact.email}`} className="text-emerald-600 hover:underline">
                      {contact.email}
                    </a>
                  </div>
                )}
                {contact?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${contact.phone}`} className="text-emerald-600 hover:underline">
                      {contact.phone}
                    </a>
                  </div>
                )}
                {contact?.employer && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="w-4 h-4 text-muted-foreground" />
                    {contact.employer}
                    {contact.job_title && ` - ${contact.job_title}`}
                  </div>
                )}
                {(contact?.city || contact?.state) && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    {[contact.city, contact.state].filter(Boolean).join(', ')}
                  </div>
                )}
                {contact?.date_of_birth && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    DOB: {contact.date_of_birth}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Summary Card */}
            {(lead.ai_summary || lead.ai_issues?.length > 0) && (
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                    AI Case Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {lead.ai_summary && (
                    <div>
                      <h4 className="font-medium text-sm mb-1">Summary</h4>
                      <p className="text-sm text-muted-foreground">{lead.ai_summary}</p>
                    </div>
                  )}
                  {lead.ai_issues?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-1">Key Issues</h4>
                      <ul className="text-sm text-muted-foreground list-disc list-inside">
                        {lead.ai_issues.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {lead.ai_pain_points?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-1">Pain Points</h4>
                      <ul className="text-sm text-muted-foreground list-disc list-inside">
                        {lead.ai_pain_points.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {lead.ai_legal_strategy && (
                    <div>
                      <h4 className="font-medium text-sm mb-1">Recommended Strategy</h4>
                      <p className="text-sm text-muted-foreground">{lead.ai_legal_strategy}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Tabs */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="consultations">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="consultations">
                  Consultations ({consultations.length})
                </TabsTrigger>
                <TabsTrigger value="documents">
                  Documents ({documents.length})
                </TabsTrigger>
                <TabsTrigger value="details">
                  Case Details
                </TabsTrigger>
              </TabsList>

              <TabsContent value="consultations" className="mt-4 space-y-4">
                {consultations.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      <Mic className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No consultations recorded yet</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => setShowRecordingDialog(true)}
                      >
                        Record First Consultation
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  consultations.map((consult) => (
                    <Card key={consult.id}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">
                            {consult.consultation_type === 'phone' ? 'Phone Call' : 
                             consult.consultation_type === 'video' ? 'Video Call' : 'In-Person Meeting'}
                          </CardTitle>
                          <Badge variant="outline">
                            {new Date(consult.created_at).toLocaleDateString()}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {consult.notes && (
                          <p className="text-sm text-muted-foreground">{consult.notes}</p>
                        )}
                        
                        {consult.audio_file_path && !consult.transcript && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => transcribeConsultation(consult.id)}
                            disabled={transcribing && selectedConsultation === consult.id}
                          >
                            {transcribing && selectedConsultation === consult.id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Transcribing...
                              </>
                            ) : (
                              <>
                                <FileText className="w-4 h-4 mr-2" />
                                Transcribe Recording
                              </>
                            )}
                          </Button>
                        )}

                        {consult.transcript && (
                          <div className="bg-slate-50 rounded-lg p-3">
                            <h4 className="font-medium text-sm mb-2">Transcript</h4>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap max-h-40 overflow-y-auto">
                              {consult.transcript}
                            </p>
                          </div>
                        )}

                        {consult.transcript && !consult.ai_summary && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => analyzeConsultation(consult.id)}
                            disabled={analyzing && selectedConsultation === consult.id}
                          >
                            {analyzing && selectedConsultation === consult.id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <Brain className="w-4 h-4 mr-2" />
                                Analyze with AI
                              </>
                            )}
                          </Button>
                        )}

                        {consult.ai_summary && (
                          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-emerald-600" />
                              AI Analysis
                            </h4>
                            <p className="text-sm text-muted-foreground">{consult.ai_summary}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="documents" className="mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Documents</CardTitle>
                      <div>
                        <input
                          type="file"
                          id="doc-upload"
                          className="hidden"
                          onChange={uploadDocument}
                        />
                        <Button variant="outline" size="sm" asChild>
                          <label htmlFor="doc-upload" className="cursor-pointer">
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Document
                          </label>
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {documents.length === 0 ? (
                      <div className="py-8 text-center text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No documents uploaded yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {documents.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-muted-foreground" />
                              <div>
                                <p className="font-medium text-sm">{doc.filename}</p>
                                <p className="text-xs text-muted-foreground">
                                  {(doc.file_size / 1024).toFixed(1)} KB • Uploaded by {doc.uploaded_by}
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline">{doc.file_type}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="details" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Case Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-muted-foreground">Case Type</Label>
                      <p className="font-medium">{lead.case_type || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Lead Source</Label>
                      <p className="font-medium">{lead.source?.replace(/_/g, ' ')}</p>
                    </div>
                    {lead.source_details && (
                      <div>
                        <Label className="text-muted-foreground">Source Details</Label>
                        <p className="font-medium">{lead.source_details}</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground">Case Description</Label>
                      <p className="text-sm">{lead.case_description || 'No description provided'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Current Stage</Label>
                      <Badge className="mt-1">{lead.stage?.replace(/_/g, ' ')}</Badge>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Created</Label>
                      <p className="text-sm">{new Date(lead.created_at).toLocaleString()}</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
